'use client';

import { c as _c } from "react/compiler-runtime";
import { LoadingButton } from '@7f-ui/components/button/loading-button';
import { InputDate } from '@7f-ui/components/form/input-date';
import { Select } from '@7f-ui/components/form/select';
import { TextArea } from '@7f-ui/components/inputs_old/text-area';
import { TextField } from '@7f-ui/components/text-field/text-field';
import { useToast } from '@7f-ui/components/toast/toast-provider';
import { getGraphQLErrorCode } from '@data-access/lib/execute';
import type { ExperienceFormSubject } from '@data-access/lib/gql/data/experience-form-subject';
import { valibotResolver } from '@hookform/resolvers/valibot';
import { baseUrl } from '@utils/utils-config';
import { addYears } from 'date-fns';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type { PropsWithChildren } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useExperiencesContactForm } from './_hooks/use-experience-contact-form';
import { TimeCombobox } from './time-combobox';
const SUBJECT_LABELS: Record<string, string> = {
  APPOINTMENT_REQUEST: 'Terminanfrage',
  OTHERS: 'Weiteres',
  PRIVATE_GROUP: 'Private Gruppe',
  QUESTION_ABOUT_EXPERIENCE: 'Fragen zum Erlebnis'
};
const FormRow = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="mb-6">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface ExperienceContactFormProps {
  experienceId: number;
  experienceName: string;
  backLocation: string;
  subject?: ExperienceFormSubject;
}
export function ExperienceContactForm(t0) {
  const $ = _c(89);
  const {
    experienceId,
    experienceName,
    backLocation,
    subject
  } = t0;
  const toast = useToast();
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const today = t1;
  const {
    validationSchema,
    initialValues,
    mapSubmission,
    sendForm,
    isSubmitting,
    subjectOptions
  } = useExperiencesContactForm(experienceId, subject);
  let t2;
  if ($[1] !== backLocation || $[2] !== router || $[3] !== toast) {
    t2 = () => {
      toast({
        type: "success",
        message: "Deine Anfrage wurde erfolgreich abgeschickt."
      });
      router.push(backLocation);
    };
    $[1] = backLocation;
    $[2] = router;
    $[3] = toast;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onSuccess = t2;
  let t3;
  if ($[5] !== toast) {
    t3 = error => {
      console.error(error);
      toast({
        type: "error",
        message: getGraphQLErrorCode(error) === "INVALID_EMAIL" ? "Deine E-Mail-Adresse ist ung\xFCltig. Bitte pr\xFCfe sie auf Tippfehler." : "Deine Anfrage konnte nicht abgeschickt werden. Bitte versuche es sp\xE4ter noch einmal."
      });
    };
    $[5] = toast;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const onError = t3;
  let t4;
  if ($[7] !== validationSchema) {
    t4 = valibotResolver(validationSchema);
    $[7] = validationSchema;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== initialValues || $[10] !== t4) {
    t5 = {
      defaultValues: initialValues,
      resolver: t4
    };
    $[9] = initialValues;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    register,
    handleSubmit,
    control,
    formState
  } = useForm(t5);
  let t6;
  if ($[12] !== experienceId || $[13] !== experienceName || $[14] !== mapSubmission || $[15] !== onError || $[16] !== onSuccess || $[17] !== sendForm) {
    t6 = data => {
      const mappedData = mapSubmission(data);
      sendForm({
        input: {
          formType: "experience-form",
          contactDetails: mappedData.contactDetails,
          title: `${SUBJECT_LABELS[mappedData.subject] ?? mappedData.subject}: ${experienceName}`,
          text: [{
            label: "Erlebnis",
            text: baseUrl(`/erlebnisse/${experienceId}`)
          }, {
            label: "Datum",
            text: mappedData.date || ""
          }, {
            label: "Uhrzeit",
            text: mappedData.time || ""
          }, {
            label: "Nachricht",
            text: mappedData.text
          }],
          experiencePublicId: experienceId
        }
      }, {
        onSuccess,
        onError
      });
    };
    $[12] = experienceId;
    $[13] = experienceName;
    $[14] = mapSubmission;
    $[15] = onError;
    $[16] = onSuccess;
    $[17] = sendForm;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== handleSubmit || $[20] !== t6) {
    t7 = handleSubmit(t6);
    $[19] = handleSubmit;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const onSubmit = t7;
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h1 className="mb-8 font-heading1">Sende uns eine Nachricht</h1>;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== register || $[24] !== subject || $[25] !== subjectOptions) {
    t9 = <FormRow>{!subject ? <Select {...register("subject")} label="Betreff" options={subjectOptions} required={true} /> : <TextField {...register("subject")} label="Betreff" type="text" value={SUBJECT_LABELS[subject] ?? subject} readOnly={true} />}</FormRow>;
    $[23] = register;
    $[24] = subject;
    $[25] = subjectOptions;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== register) {
    t10 = register("experienceName");
    $[27] = register;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== experienceName || $[30] !== t10) {
    t11 = <FormRow><TextField {...t10} label="Erlebnis" type="text" value={experienceName} readOnly={true} /></FormRow>;
    $[29] = experienceName;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = t13 => {
      const {
        field: t14,
        fieldState: t15
      } = t13;
      const {
        value,
        onChange
      } = t14;
      const {
        error: error_0
      } = t15;
      return <InputDate value={value} label="Wunschdatum" minDate={today} maxDate={addYears(today, 2)} required={true} error={error_0?.message} onChange={onChange} />;
    };
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== control) {
    t13 = <FormRow><Controller control={control} name="date" render={t12} /></FormRow>;
    $[33] = control;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== control) {
    t14 = <FormRow><Controller control={control} name="time" render={_temp} /></FormRow>;
    $[35] = control;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== register) {
    t15 = register("personCountDetails");
    $[37] = register;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15) {
    t16 = <FormRow><TextField {...t15} label="Anzahl Personen" /></FormRow>;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== register) {
    t17 = register("message");
    $[41] = register;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const t18 = formState.errors.message?.message;
  let t19;
  if ($[43] !== t17 || $[44] !== t18) {
    t19 = <FormRow><TextArea {...t17} label="Deine Nachricht" className="min-h-40" required={true} error={t18} /></FormRow>;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t11 || $[47] !== t13 || $[48] !== t14 || $[49] !== t16 || $[50] !== t19 || $[51] !== t9) {
    t20 = <fieldset className="mb-12">{t9}{t11}{t13}{t14}{t16}{t19}</fieldset>;
    $[46] = t11;
    $[47] = t13;
    $[48] = t14;
    $[49] = t16;
    $[50] = t19;
    $[51] = t9;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <legend className="mb-6 font-heading3">Kontaktdaten</legend>;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== register) {
    t22 = register("contactDetails.emailAddress");
    $[54] = register;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  const t23 = formState.errors.contactDetails?.emailAddress?.message;
  let t24;
  if ($[56] !== t22 || $[57] !== t23) {
    t24 = <FormRow><TextField {...t22} label="E-Mail-Adresse" type="email" required={true} error={t23} /></FormRow>;
    $[56] = t22;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== register) {
    t25 = register("contactDetails.firstName");
    $[59] = register;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  const t26 = formState.errors.contactDetails?.firstName?.message;
  let t27;
  if ($[61] !== t25 || $[62] !== t26) {
    t27 = <FormRow><TextField {...t25} label="Vorname" error={t26} /></FormRow>;
    $[61] = t25;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== register) {
    t28 = register("contactDetails.lastName");
    $[64] = register;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  const t29 = formState.errors.contactDetails?.lastName?.message;
  let t30;
  if ($[66] !== t28 || $[67] !== t29) {
    t30 = <FormRow><TextField {...t28} label="Nachname" error={t29} /></FormRow>;
    $[66] = t28;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== register) {
    t31 = register("contactDetails.phone");
    $[69] = register;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  const t32 = formState.errors.contactDetails?.phone?.message;
  let t33;
  if ($[71] !== t31 || $[72] !== t32) {
    t33 = <FormRow><TextField {...t31} label="Telefonnummer" error={t32} /></FormRow>;
    $[71] = t31;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t24 || $[75] !== t27 || $[76] !== t30 || $[77] !== t33) {
    t34 = <fieldset className="mb-2">{t21}{t24}{t27}{t30}{t33}</fieldset>;
    $[74] = t24;
    $[75] = t27;
    $[76] = t30;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <p className="font-body-small text-primary opacity-30">*Pflichtfeld</p>;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <p className="mt-8 mb-4 font-body-small">{"Wir verarbeiten deine Daten gem\xE4\xDF unserer "}<Link href="/datenschutz" className="underline underline-offset-2" target="_blank">Datenschutzerklärung</Link>.</p>;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      root: "w-full sm:w-auto"
    };
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== isSubmitting) {
    t38 = <LoadingButton isLoading={isSubmitting} type="submit" size="medium" classNames={t37}>Abschicken</LoadingButton>;
    $[82] = isSubmitting;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== onSubmit || $[85] !== t20 || $[86] !== t34 || $[87] !== t38) {
    t39 = <>{t8}<form id="experience-contact-form" className="mb-10 sm:mb-8" noValidate={true} onSubmit={onSubmit}>{t20}{t34}{t35}{t36}{t38}</form></>;
    $[84] = onSubmit;
    $[85] = t20;
    $[86] = t34;
    $[87] = t38;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  return t39;
}
function _temp(t0) {
  const {
    field: t1,
    fieldState: t2
  } = t0;
  const {
    value: value_0,
    onChange: onChange_0
  } = t1;
  const {
    error: error_1
  } = t2;
  return <TimeCombobox value={value_0} label="Wunschzeit" required={true} onChange={onChange_0} error={error_1?.message} />;
}